import React, {useState, useEffect, useCallback} from "react";
import {Form, Modal, Input, Select, Button, Table, Tag, message, Card, Tabs, Typography, Tooltip} from "antd";
import {ReloadOutlined, SaveOutlined, EditOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import weekOfYear from "dayjs/plugin/weekOfYear";
import apiClient from './apiHelper';
import {ENDPOINTS, REQUEST_TYPES, MESSAGES} from './apiConfig';
import {dataEntryBaseColumns} from './constants';
import {DataEntryStatusFilter} from './FilterBar';

const {Title} = Typography;

dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

const DataEntry = ({user}) => {
    const [loadingRows, setLoadingRows] = useState({});
    const [monthTasks, setMonthTasks] = useState([]);
    const [userDetails, setUserDetails] = useState();
    const [allTasks, setAllTasks] = useState([]);
    const [taskLoading, setTaskLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("submit");
    const [pendingFilter, setPendingFilter] = useState("all");
    const [pendingTabTasks, setPendingTabTasks] = useState([]);
    const [pendingTabLoading, setPendingTabLoading] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm] = Form.useForm();

    const fetchUserDetails = useCallback(async () => {
        try {
            const res = await apiClient(ENDPOINTS.USER.FETCH_USER_DETAILS, {
                method: "POST",
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_USER_DETAILS
                }),
            });
            const userList = res?.items || res || [];
            setUserDetails(userList);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_USER_DETAILS);
        }
    }, []);

    const fetchAllTasks = useCallback(async () => {
        setTaskLoading(true);
        try {
            const response = await apiClient(ENDPOINTS.TASK.FETCH_ALL_TASKS, {
                method: "POST",
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_ALL_TASKS,
                }),
            });
            const taskData = Array.isArray(response)
                ? response
                : (response?.data || response?.items || []);

            setAllTasks(taskData);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_ALL_TASKS);
        } finally {
            setTaskLoading(false);
        }
    }, []);

    const isWithinWorkWindow = (dateToCheck, workStartDate, workEndDate) => {
        const d = dayjs(dateToCheck).startOf('day').valueOf();
        const start = dayjs(workStartDate).startOf('day').valueOf();
        const end = dayjs(workEndDate).startOf('day').valueOf();
        return d >= start && d <= end;
    };

    const workStartDate = userDetails?.work_start_date;
    const workEndDate = userDetails?.work_end_date;

    const initializeMonthRows = useCallback(() => {
        if (!workStartDate || !workEndDate) {
            setMonthTasks([]);
            return;
        }

        const isBackfillValid = userDetails?.can_backfill;
        const searchStartDate = isBackfillValid
            ? dayjs(workStartDate)
            : dayjs().startOf("month");

        const endOfRange = dayjs().endOf("month");
        const sevenDaysAgo = dayjs().subtract(7, 'day').startOf('day');
        const submittedDates = getSubmittedDatesSet(allTasks);
        const projectList = Array.isArray(userDetails?.project) ? userDetails.project : [];
        const defaultProject = projectList.length > 0 ? projectList[0] : "";

        const rows = [];
        const totalDaysToScan = endOfRange.diff(searchStartDate, 'day') + 1;

        for (let i = 0; i < totalDaysToScan; i++) {
            const currentDate = searchStartDate.add(i, "day");
            const formattedDate = currentDate.format("YYYY-MM-DD");
            const dayName = currentDate.format("dddd");

            if (submittedDates.has(formattedDate)) continue;
            if (!isWithinWorkWindow(currentDate, workStartDate, workEndDate)) continue;

            const isBeforeSevenDays = currentDate.isBefore(sevenDaysAgo);
            const isDisabled = isBeforeSevenDays && !isBackfillValid;

            rows.push({
                key: formattedDate,
                date: formattedDate,
                day: dayName,
                project: defaultProject,
                task: "",
                summary: "",
                type: "Productive Day",
                isDisabled: isDisabled,
            });
        }

        setMonthTasks(rows);
    }, [allTasks, userDetails, workStartDate, workEndDate]);

    const handleInputChange = (key, field, value) => {
        const newData = [...monthTasks];
        const index = newData.findIndex((item) => key === item.key);
        if (index > -1) {
            newData[index][field] = value;
            setMonthTasks(newData);
        }
    };

    const submitRow = async (record) => {

        const today = dayjs().startOf('day');
        const selectedDate = dayjs(record.date, 'YYYY-MM-DD').startOf('day');

        const taskValidate = ["Week Off", "Holiday", "Leave"].includes(record.type);

        if (!record.project || (Array.isArray(record.project) && record.project.length === 0)) {
            return message.warning("Please select at least one project");
        }

        if (!taskValidate && !record.task) {
            return message.warning("Please fill in Task field");
        }

        if (selectedDate.isAfter(today)) {
            if (record.type !== "Leave") {
                return message.warning("For future dates, you can only submit leave applications.");
            }
        }

        const formattedDate = record.date;
        const dayName = record.day;

        setLoadingRows(prev => ({...prev, [record.key]: true}));
        try {
            const payload = {
                request_type: REQUEST_TYPES.CREATE_TASK,
                project: record.project,
                task: record.task,
                summary: record.summary,
                type: record.type,
                date: formattedDate,
                day: dayName
            };

            await apiClient(ENDPOINTS.TASK.CREATE_TASK, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            message.success(`Task for ${record.date} submitted!`);
            setMonthTasks(prev => prev.filter(item => item.key !== record.key));
        } catch (error) {
            message.error(MESSAGES.ERROR.CREATE_TASK);
        } finally {
            setLoadingRows(prev => ({...prev, [record.key]: false}));
        }
    };

    const getSubmittedDatesSet = (tasks) => {
        return new Set(tasks.map(t => dayjs(t.date).format("YYYY-MM-DD")));
    };

    const fetchPendingTabTasks = async (statusValue = "all") => {
        setPendingTabLoading(true);
        try {
            const body = {
                request_type: REQUEST_TYPES.FETCH_ALL_TASKS,
                user,
            };

            if (statusValue && statusValue !== "all") {
                body.status = statusValue;
            }

            const response = await apiClient(ENDPOINTS.TASK.FETCH_ALL_TASKS, {
                method: "POST",
                body: JSON.stringify(body),
            });
            const taskData = Array.isArray(response)
                ? response
                : (response?.data || response?.items || []);

            setPendingTabTasks(taskData);

        } catch (e) {
            message.error(MESSAGES.ERROR.FETCH_ALL_TASKS);
        } finally {
            setPendingTabLoading(false);
        }
    };

    const handlePendingFilterChange = (value) => {
        setPendingFilter(value);
        fetchPendingTabTasks(value);
    };

    const handleUpdate = (record) => {
        setEditRecord(record);
        editForm.setFieldsValue({
            task: record.task,
            summary: record.summary,
            type: record.type,
            project: record.project,
            date: record.date,
        });
        setIsEditModalOpen(true);
    };

    const submitUpdate = async (values) => {
        if (!editRecord) return;

        const today = dayjs().startOf('day');
        const recordDate = dayjs(editRecord.date, 'YYYY-MM-DD').startOf('day');

        if (recordDate.isAfter(today)) {
            if (values.type !== "Leave") {
                return message.warning("For future dates, you can only submit leave applications.");
            }
        }

        setEditLoading(true);
        try {
            const payload = {
                request_type: REQUEST_TYPES.UPDATE_TASK,
                task_id: editRecord.task_id,
                type: values.type,
                task: ['Holiday', 'Week Off', 'Leave'].includes(values.type) ? "" : values.task,
                summary: values.summary,
                project: values.project
            };

            await apiClient(ENDPOINTS.TASK.UPDATE_TASK, {
                method: "POST",
                body: JSON.stringify(payload),
            });

            message.success(MESSAGES.SUCCESS.UPDATE_TASK);
            setIsEditModalOpen(false);

            if (typeof fetchPendingTabTasks === "function") {
                await fetchPendingTabTasks(pendingFilter);
            }

        } catch (e) {
            message.error(MESSAGES.ERROR.UPDATE_TASK);
        } finally {
            setEditLoading(false);
        }
    };

    const submitColumns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            ellipsis: true,
            width: 110
        },
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            width: 90,
            ellipsis: {showTitle: false},
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
        },
        {
            title: "Project",
            dataIndex: "project",
            key: "project",
            width: 160,
            render: (val, record) => {
                const projectOptions = Array.isArray(userDetails?.project)
                    ? userDetails.project.map(p => ({
                        label: p,
                        value: p
                    }))
                    : [];
                const selectedValues = Array.isArray(val)
                    ? val
                    : (val ? val.split(',').map(p => p.trim()) : []);

                return (
                    <Select
                        mode="multiple"
                        placeholder="Select Projects"
                        disabled={record.isDisabled}
                        value={selectedValues}
                        style={{width: "100%"}}
                        onChange={(value) => handleInputChange(record.key, "project", value)}
                        options={projectOptions}
                        maxTagCount="responsive"
                    />
                );
            }
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 160,
            render: (val, record) => (
                <Select
                    placeholder="Select Type"
                    disabled={record.isDisabled}
                    value={record.type}
                    style={{width: "100%"}}
                    onChange={(value) =>
                        handleInputChange(record.key, "type", value)
                    }
                    defaultValue="Productive Day"
                    options={[
                        {value: "Productive Day", label: "Productive Day"},
                        {value: "Half Day", label: "Half Day"},
                        {value: "Week Off", label: "Week Off"},
                        {value: "Holiday", label: "Holiday"},
                        {value: "Leave", label: "Leave"},
                    ]}
                />
            ),
        },
        {
            title: "Task",
            dataIndex: "task",
            key: "task",
            width: 150,
            ellipsis: true,
            render: (val, record) => {
                const isLocked = record.isDisabled || ["Week Off", "Leave", "Holiday"].includes(record.type);

                return (
                    <Input.TextArea
                        placeholder="Task"
                        autoSize={{minRows: 1, maxRows: 6}}
                        disabled={isLocked}
                        value={val}
                        className="custom-task-input"
                        onChange={(e) => handleInputChange(record.key, "task", e.target.value)}
                    />
                );
            },
        },
        {
            title: "Task Summary (Optional)",
            dataIndex: "summary",
            key: "summary",
            width: 180,
            ellipsis: true,
            render:
                (val, record) => {
                    const isLocked = record.isDisabled || ["Week Off", "Leave", "Holiday"].includes(record.type);

                    return (

                        <Input.TextArea
                            placeholder="Task Summary"
                            autoSize={{minRows: 1, maxRows: 6}}
                            disabled={isLocked}
                            value={val}
                            onChange={(e) =>
                                handleInputChange(record.key, "summary", e.target.value)
                            }
                        />
                    );
                }
        },
        {
            title: "Action",
            key: "action",
            onCell: (record) => ({
                className: record.isDisabled ? "disabled-action" : "",
            }),
            width: 110,
            render: (_, record) => (
                <div className="w-full">
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        disabled={record.isDisabled}
                        loading={loadingRows[record.key]}
                        onClick={() => submitRow(record)}
                        className="w-full"
                        danger
                    >
                        Submit
                    </Button>
                </div>
            ),
        }
    ];

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            width: 110
        },
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            width: 80,
            ellipsis: {showTitle: false},
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
        },
        {
            title: "Project",
            dataIndex: "project",
            key: "project",
            width: 100,
            render: (projectValue) => {
                const selectedProjects = Array.isArray(projectValue)
                    ? projectValue
                    : (projectValue ? projectValue.split(',').map(p => p.trim()) : []);

                if (selectedProjects.length === 0) return <span style={{color: '#bfbfbf'}}>N/A</span>;

                return (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                        {selectedProjects.map((p) => (
                            <Tooltip title={p} key={p} mouseEnterDelay={0.5}>
                                <Tag
                                    color="gray"
                                    key={p}
                                    style={{
                                        margin: 0,
                                        fontSize: '11px',
                                        maxWidth: '100px',
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'bottom'
                                    }}
                                >
                                    {p}
                                </Tag>
                            </Tooltip>
                        ))}
                    </div>
                );
            },
        },
        ...dataEntryBaseColumns,
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => (
                <Tag color={status === "approved" ? "green" : status === "pending" ? "blue" : "orange"}>
                    {status?.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: "Action",
            key: "useraction",
            width: 100,
            render: (_, record) => {
                const isPending = record.status === "pending";
                return (
                    <Button
                        type="primary"
                        icon={<EditOutlined/>}
                        disabled={!isPending}
                        onClick={() => handleUpdate(record)}
                        danger
                    >
                        Update
                    </Button>
                );
            },
        }
    ];

    useEffect(() => {
        if (user) {
            fetchUserDetails();
        }
    }, [user, fetchUserDetails]);

    useEffect(() => {
        if (userDetails?.work_start_date && userDetails?.work_end_date && Array.isArray(allTasks)) {
            initializeMonthRows();
        }
    }, [initializeMonthRows]);

    useEffect(() => {
        fetchAllTasks();
    }, [fetchAllTasks]);

    return (
        <div>
            <Card title={<Title level={4} style={{marginBottom: 0}}>Time Sheet</Title>}>
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        if (key === "pending") fetchPendingTabTasks(pendingFilter);
                    }}
                    items={[
                        {
                            key: "submit",
                            label: "Submit Daily Activity",
                            children: (
                                <>
                                    <div className="flex justify-end mb-4">
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            onClick={() => fetchAllTasks()}
                                            loading={taskLoading}
                                        >
                                            Refresh
                                        </Button>
                                    </div>
                                    <Table
                                        columns={submitColumns}
                                        dataSource={monthTasks}
                                        loading={taskLoading}
                                        rowKey="key"
                                        pagination={false}
                                        tableLayout="fixed"
                                        onRow={(record) => ({
                                            style: {
                                                background: record.isDisabled ? '#f5f5f5' : 'inherit',
                                                cursor: record.isDisabled ? 'not-allowed' : 'default',
                                            },
                                        })}
                                    />
                                </>
                            ),
                        },
                        {
                            key: "pending",
                            label: "Your Tasks",
                            children: (
                                <>
                                    <div className="flex justify-between items-center mb-4 gap-2">
                                        <DataEntryStatusFilter
                                            value={pendingFilter}
                                            onChange={handlePendingFilterChange}
                                        />
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            onClick={() => fetchPendingTabTasks(pendingFilter)}
                                            loading={pendingTabLoading}
                                        >
                                            Refresh
                                        </Button>
                                    </div>

                                    <Table
                                        columns={columns}
                                        dataSource={pendingTabTasks}
                                        rowKey="task_id"
                                        loading={pendingTabLoading}
                                        pagination={{pageSize: 10}}
                                        tableLayout="fixed"
                                        locale={{emptyText: "No tasks found for the selected filter"}}
                                    />
                                </>
                            ),
                        },
                    ]}
                />
            </Card>

            <Modal
                title="Update Task"
                open={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                okText="Update"
                okButtonProps={{danger: true}}
                onOk={() => editForm.submit()}
                confirmLoading={editLoading}
                centered
                destroyOnClose
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={submitUpdate}
                >
                    <Form.Item label="Date">
                        <Input value={editRecord?.date} disabled/>
                    </Form.Item>

                    <Form.Item
                        label="Project"
                        name="project"
                        rules={[{required: true, message: 'Please select at least one project'}]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select Projects"
                            options={Array.isArray(userDetails?.project)
                                ? userDetails.project.map(p => ({label: p, value: p}))
                                : []
                            }
                            maxTagCount="responsive"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Type"
                        name="type"
                    >
                        <Select
                            placeholder="Select Type"
                            style={{width: "100%"}}
                            defaultValue="Productive Day"
                            value={editRecord?.type}
                            options={[
                                {value: "Week Off", label: "Week Off"},
                                {value: "Holiday", label: "Holiday"},
                                {value: "Leave", label: "Leave"},
                                {value: "Productive Day", label: "Productive Day"},
                                {value: "Half Day", label: "Half Day"},
                            ]}
                        />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
                    >
                        {({getFieldValue}) => {
                            const type = getFieldValue('type');
                            const isDisabled = ["Week Off", "Holiday", "Leave"].includes(type);

                            return (
                                <Form.Item
                                    label="Task"
                                    name="task"
                                    rules={[{
                                        required: !isDisabled,
                                        message: "Please enter task"
                                    }]}
                                >
                                    <Input.TextArea
                                        placeholder={isDisabled ? "No task required" : "Update task"}
                                        autoSize={{minRows: 1, maxRows: 4}}
                                        disabled={isDisabled}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
                    >
                        {({getFieldValue}) => {
                            const type = getFieldValue('type');
                            const isDisabled = ['Holiday', 'Week Off', 'Leave'].includes(type);

                            return (
                                <Form.Item
                                    label="Task Summary"
                                    name="summary"
                                >
                                    <Input.TextArea
                                        placeholder={isDisabled ? "No summary required" : "Update summary"}
                                        autoSize={{minRows: 2, maxRows: 6}}
                                        disabled={isDisabled}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DataEntry;